import {useCallback, useEffect} from 'react';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {useIntl} from 'react-intl';
import {Text, Container, Flex, Button, useToast, Grid, Divider, Box, ButtonProps, FlexProps} from '@chakra-ui/react';
import {motion} from 'framer-motion';

import {useTranslate} from '@eksab/i18n';
import {getLanguage, prefetchImage} from '@eksab/util';

import {StarHalfIcon} from '@eksab/features/profile/icons';

import {Question, QuizHeader, CheckmarkAnimation, Answers} from './';
import {useTriviaStore} from '../state';
import {useInvalidateChallenges, useSubmitAnswerMutation} from '../hooks';
import {ArrowForwardTripleIcon} from '../icons';
import type {SubmitAnswerParams, SubmitAnswerResponse} from '../types';

export function Quiz() {
  const toast = useToast();
  const invalidateChallenges = useInvalidateChallenges();

  const t = useTranslate();
  const challenge = useTriviaStore((state) => state.challenge);
  const question = useTriviaStore((state) => state.currentQuestion!);
  const attemptId = useTriviaStore((state) => state.attemptId);
  const onSubmitAnswerSuccess = useTriviaStore((state) => state.onSubmitAnswerSuccess);
  const onOutOfTime = useTriviaStore((state) => state.onOutOfTime);
  const isClosed = useTriviaStore((state) => state.isClosed);

  const isWE = challenge?.game === 'trivia-misc-we';

  const submitAnswerMutation = useSubmitAnswerMutation(attemptId, {
    retry: (failureCount, error) => {
      if (['ATTEMPT_TIMEOUT', 'ATTEMPT_NOT_FOUND'].indexOf(error.response?.data.errorCode!) !== -1) {
        return false;
      }
      return failureCount < 3;
    },
  });

  const onMutationSuccess = (data: SubmitAnswerResponse, {answers}: SubmitAnswerParams) => {
    // Play success sound on correct and set
    if (data.is_correct) {
      const audio = new Audio('/sounds/right-answer.wav');
      audio.volume = 0.4;
      audio.play();
    } else {
      const audio = new Audio('/sounds/wrong-answer.wav');
      audio.volume = 0.4;
      audio.play();
    }

    onSubmitAnswerSuccess(data, answers);

    if (['winner', 'loser'].includes(data.state)) {
      invalidateChallenges();
    }

    if (data.state === 'go_to_next_question') {
      // If next question and/or answer type is image, prefetch the image(s) during the success animation.
      if (data.next_question.type === 'image') prefetchImage(data.next_question.image);
      if (data.next_question.answers_type === 'image') {
        data.next_question.answers.forEach((answer) => prefetchImage(answer.media_url));
      }
    }
  };

  const onAnimationEnded = () => {
    // Must be there, as animation ends after setNextQuestion is called
    onSubmitAnswerSuccess(submitAnswerMutation.data!, submitAnswerMutation.variables?.answers!);
  };

  const submitAnswer = useCallback(() => {
    const selectedAnswers = useTriviaStore.getState().currentAnswer;

    submitAnswerMutation.mutate(
      {answers: selectedAnswers, questionID: question.id},
      {
        onSuccess: onMutationSuccess,
        onError: (e) => {
          if (['ATTEMPT_TIMEOUT', 'ATTEMPT_NOT_FOUND'].indexOf(e.response?.data.errorCode!) !== -1) {
            return onOutOfTime();
          }

          toast({status: 'error', title: e.response?.data?.message ?? t('app.errors.try-again')});
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, submitAnswerMutation.isLoading]);

  useEffect(() => {
    const beforeUnloadListener = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    // Confirm if user wants to leave
    window.addEventListener('beforeunload', beforeUnloadListener, {capture: true});

    return () => {
      window.removeEventListener('beforeunload', beforeUnloadListener, {capture: true});
    };
  }, []);

  return (
    <Container maxH="100dvh" flex={1} flexDir="column" pt="4" pb="12" m="0" maxW="unset" position="relative">
      <Head>
        <title>{`${challenge?.title} - ${t('app.trivia')} | ${t('app.name')}`}</title>
      </Head>

      {submitAnswerMutation.data?.state === 'go_to_next_question' && submitAnswerMutation.data?.is_correct && (
        <Box
          as={motion.div}
          position="absolute"
          zIndex={1000}
          inset={0}
          animate={{
            backgroundColor: 'rgba(0,0,0,0.1)',
            transition: {
              duration: 0.2,
              delay: 0.2,
              ease: 'linear',
            },
          }}
          w="100%"
          h="100%"
        >
          <CheckmarkAnimation onEnded={onAnimationEnded} />
        </Box>
      )}

      <QuizHeader isTimerPaused={isClosed || !submitAnswerMutation.isIdle} onTimeOut={submitAnswer} mb={[0.5, 1, 2]} />

      {!isWE && <QuestionPoints alignSelf="center" />}

      <Grid
        flex={{lg: 1}}
        templateColumns={{lg: '1fr auto 1fr'}}
        rowGap="4"
        columnGap="12"
        placeItems="center"
        mt={[2, 4]}
        mb="4"
      >
        <Question mt="2" w="full" maxW={{lg: '30rem'}} />

        <Divider orientation="vertical" display={{base: 'none', lg: 'block'}} maxH="60%" />

        <Answers w="full" maxW={{lg: 96}} submitAnswerMutation={submitAnswerMutation} />
      </Grid>

      <SubmitAnswerButton
        mt="auto"
        alignSelf="center"
        isLoading={submitAnswerMutation.isLoading}
        onClick={submitAnswer}
      />
    </Container>
  );
}

const QuestionPoints = (props: FlexProps) => {
  const format = useIntl().formatNumber;

  const t = useTranslate();
  const points = useTriviaStore((state) => state.currentQuestion!.points);

  if (!points) return null;

  return (
    <Flex align="center" gap="0.5" p="2" rounded="full" bg="#e6f6eb" {...props}>
      <StarHalfIcon />

      <Text fontSize="md" fontWeight="500" color="#408c57">
        {t('app.points.full', {
          points: (
            <Text as="b" fontWeight="800" color="inherit">
              +{format(points)}
            </Text>
          ),
        })}
      </Text>
    </Flex>
  );
};

const SubmitAnswerButton = (props: ButtonProps) => {
  const {locale} = useRouter();

  const t = useTranslate();
  const choseRequiredNumOfAnswers = useTriviaStore(
    (state) => state.currentAnswer.length === state.currentQuestion?.correct_answers_count,
  );
  const isLastQuestion = useTriviaStore((state) => state.answeredQuestions === state.challenge?.no_of_questions);

  return (
    <Button
      variant="unstyled"
      w="full"
      maxW={{lg: '32rem'}}
      display="flex"
      justifyContent="space-between"
      gap="4"
      bg="green.400"
      color="white"
      minH="20"
      paddingInline="6"
      rounded="3xl"
      fontSize="xl"
      fontWeight="700"
      transform="auto"
      _hover={{bg: 'green.300'}}
      _active={{translateY: 1, bg: 'green.400'}}
      _loading={{justifyContent: 'center', _active: {translateY: 0}}}
      _disabled={{bg: '#8f8f8f', cursor: 'not-allowed', _hover: {bg: '#8f8f8f'}, _active: {translateY: 0}}}
      {...props}
      isDisabled={!choseRequiredNumOfAnswers || props.isLoading}
    >
      {t(`trivia.quiz.cta.${isLastQuestion ? 'last' : 'next'}`)}

      <ArrowForwardTripleIcon transform="auto" scaleX={getLanguage(locale) === 'en' ? 1 : -1} />
    </Button>
  );
};
